import { CsvBulkConfig, CsvFieldDefinition } from 'components/csv/types';
import { MasterTypeResult } from 'stores/MasterTypeStoreAdapter';
import { CreateMemberParams, createMember } from 'stores/MemberStoreAdapter';
import { PartnerResult } from 'stores/PartnerStoreAdapter';
import { UserResult } from 'stores/UserStoreAdapter';

/**
 * 要員CSV一括登録用のフィールド定義を生成する
 */
export const createMemberCsvFields = (
  employmentTypes: MasterTypeResult[],
  genders: MasterTypeResult[],
  contractTypes: MasterTypeResult[],
  partners: PartnerResult[],
  users: UserResult[],
): CsvFieldDefinition[] => [
  {
    header: '要員名',
    key: 'memberName',
    description: '要員の氏名（必須、最大300文字）',
    required: true,
    type: 'text',
    maxLength: 300,
  },
  {
    header: '要員名（カナ）',
    key: 'memberNameKana',
    description: '要員の氏名カナ（必須、最大300文字）',
    required: true,
    type: 'text',
    maxLength: 300,
  },
  {
    header: '雇用形態ID',
    key: 'employmentTypeId',
    description: '雇用形態のコード値（必須）',
    required: true,
    type: 'radio',
    options: employmentTypes.map((t) => ({ code: t.typeCode, label: t.typeLabel })),
  },
  {
    header: 'ユーザーID',
    key: 'userId',
    description: '社員の場合に紐付けるユーザーID（任意）',
    required: false,
    type: 'id',
    maxLength: 300,
    options: users.map((u) => ({ code: u.userId, label: u.userName })),
  },
  {
    header: '性別ID',
    key: 'genderId',
    description: '性別のコード値（必須）',
    required: true,
    type: 'radio',
    options: genders.map((g) => ({ code: g.typeCode, label: g.typeLabel })),
  },
  {
    header: '個人事業主フラグ',
    key: 'isIndividual',
    description: 'true/false（社員の場合はfalse固定）',
    required: true,
    type: 'check',
    options: [
      { code: 'true', label: 'はい（個人事業主）' },
      { code: 'false', label: 'いいえ（法人所属）' },
    ],
  },
  {
    header: '所属',
    key: 'department',
    description: '所属部署（社員の場合に任意で設定、最大300文字）',
    required: false,
    type: 'text',
    maxLength: 300,
  },
  {
    header: 'パートナーID',
    key: 'partnerId',
    description: '社外要員の場合に所属するパートナーのID（最大64文字）',
    required: false,
    type: 'id',
    maxLength: 64,
    options: partners.map((p) => ({ code: p.partnerId, label: p.partnerName })),
  },
  {
    header: '契約形態ID',
    key: 'contractTypeId',
    description: '社外要員の契約形態コード値',
    required: false,
    type: 'select',
    options: contractTypes
      .filter((t) => t.typeCode !== 'EMPLOYEE')
      .map((t) => ({ code: t.typeCode, label: t.typeLabel })),
  },
  {
    header: '備考',
    key: 'remarks',
    description: '備考（任意、最大10000文字）',
    required: false,
    type: 'text',
    maxLength: 10000,
  },
];

/**
 * 要員CSV一括登録のConfig生成関数
 */
export const createMemberBulkConfig = (
  employmentTypes: MasterTypeResult[],
  genders: MasterTypeResult[],
  contractTypes: MasterTypeResult[],
  partners: PartnerResult[],
  users: UserResult[],
): CsvBulkConfig<CreateMemberParams> => {
  const fields = createMemberCsvFields(employmentTypes, genders, contractTypes, partners, users);

  return {
    entityName: '要員',
    fields,
    toApiParams: (row: Record<string, string>): CreateMemberParams => {
      const isIndividualValue =
        row.isIndividual === 'true' || row.isIndividual === '1' || row.isIndividual === 'TRUE';

      const params: CreateMemberParams = {
        memberName: row.memberName,
        memberNameKana: row.memberNameKana,
        employmentTypeId: row.employmentTypeId,
        genderId: row.genderId,
        isIndividual: isIndividualValue,
      };

      if (row.userId) params.userId = row.userId;
      if (row.department) params.department = row.department;
      if (row.partnerId) params.partnerId = row.partnerId;
      if (row.contractTypeId) params.contractTypeId = row.contractTypeId;
      if (row.remarks) params.remarks = row.remarks;

      return params;
    },
    registerOne: createMember,
    resolveDisplayValue: (key: string, rawValue: string): string | null => {
      if (!rawValue) return null;

      const field = fields.find((f) => f.key === key);
      if (!field || !field.options) return null;

      if (field.type === 'check') {
        if (rawValue === 'true' || rawValue === '1' || rawValue === 'TRUE') return 'はい';
        if (rawValue === 'false' || rawValue === '0' || rawValue === 'FALSE') return 'いいえ';
        return null;
      }

      const option = field.options.find((o) => o.code === rawValue);
      return option ? option.label : null;
    },
    validateField: (key: string, value: string, row: Record<string, string>): string | null => {
      const isEmployee = row.employmentTypeId === 'EMPLOYEE';
      const isIndividualTrue =
        row.isIndividual === 'true' || row.isIndividual === '1' || row.isIndividual === 'TRUE';

      // === 社員なのにありえないパターン ===

      // 社員なのに個人事業主フラグがtrue
      if (key === 'isIndividual' && isEmployee && isIndividualTrue) {
        return '社員の場合、個人事業主フラグはfalseにしてください';
      }

      // 社員なのにパートナーIDが入力されている
      if (key === 'partnerId' && isEmployee && value) {
        return '社員の場合、パートナーIDは入力できません';
      }

      // 社員なのに契約形態IDが入力されている
      if (key === 'contractTypeId' && isEmployee && value) {
        return '社員の場合、契約形態IDは入力できません';
      }

      // === 社外なのにありえないパターン ===

      // 社外なのにユーザーIDが入力されている
      if (key === 'userId' && !isEmployee && value) {
        return '社外要員の場合、ユーザーIDは入力できません';
      }

      // 社外なのに所属が入力されている
      if (key === 'department' && !isEmployee && value) {
        return '社外要員の場合、所属は入力できません';
      }

      // === 社外要員の必須チェック ===

      // 社外要員の場合、パートナーIDが必須
      if (key === 'partnerId' && !isEmployee && !value) {
        return '社外要員の場合、パートナーIDは必須です';
      }

      // 社外要員の場合、契約形態IDが必須
      if (key === 'contractTypeId' && !isEmployee && !value) {
        return '社外要員の場合、契約形態IDは必須です';
      }

      // === ID存在チェック ===

      // ユーザーIDの存在チェック
      if (key === 'userId' && value) {
        const userExists = users.some((u) => u.userId === value);
        if (!userExists) return `ユーザーID「${value}」は存在しません`;
      }

      // パートナーIDの存在チェック
      if (key === 'partnerId' && value) {
        const partnerExists = partners.some((p) => p.partnerId === value);
        if (!partnerExists) return `パートナーID「${value}」は存在しません`;
      }

      return null;
    },
  };
};
